import axios from 'axios';

import { uebersetze } from '../i18n/sprache.js';

import { feldFehlerMeldung } from './feldfehler.js';

// Default to the current page origin so the request goes through whatever
// proxy is serving the bundle (Vite dev server, Electron, Cloudflare tunnel, …).
// Setting VITE_API_BASE_URL to an absolute URL overrides this - only use that
// when the gateway is not reachable through the same origin.
const baseURL = import.meta.env.VITE_API_BASE_URL ?? '';

export const apiClient = axios.create({
  baseURL,
  timeout: 20_000,
  headers: { 'Content-Type': 'application/json' },
});

let accessToken = null;
let onUnauthorized = null;
let onForbidden = null;

export function setForbiddenHandler(handler) {
  onForbidden = handler;
}

export function setAccessToken(token) {
  accessToken = token;
}

export function getAccessToken() {
  return accessToken;
}

export function setUnauthorizedHandler(handler) {
  onUnauthorized = handler;
}

apiClient.interceptors.request.use((config) => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

/**
 * Wie lange noch? `express-rate-limit` schickt `Retry-After` in Sekunden,
 * manche Zwischenstellen ein Datum. Beides wird zu aufgerundeten Sekunden -
 * und alles Unverständliche zu null, damit daraus nie ein "in NaN Sekunden"
 * wird.
 */
export function wartezeitSekunden(headers) {
  const roh = headers?.['retry-after'] ?? headers?.['Retry-After'];
  if (roh === undefined || roh === null || roh === '') {
    return null;
  }
  const zahl = Number(roh);
  if (Number.isFinite(zahl)) {
    return zahl > 0 ? Math.ceil(zahl) : null;
  }
  const zeitpunkt = Date.parse(roh);
  if (Number.isNaN(zeitpunkt)) {
    return null;
  }
  const sekunden = Math.ceil((zeitpunkt - Date.now()) / 1000);
  return sekunden > 0 ? sekunden : null;
}

/**
 * Statuswerte in den Einsetzungen mit übersetzen.
 *
 * Ein Dienst schickt `vars: { status: 'DRAFT', erlaubt: 'SENT' }` - das sind
 * die Werte, wie sie in der Datenbank stehen. Ohne diesen Schritt stünde
 * "Das geht nur bei SENT - diese Offerte ist DRAFT" auf dem Bildschirm: halb
 * übersetzt ist schlechter als gar nicht, weil es aussieht wie ein Fehler
 * im Text und nicht wie eine Auskunft.
 *
 * Übersetzt wird NUR, was aussieht wie ein Statuswert (GROSSBUCHSTABEN mit
 * Unterstrich) und wofür es ein Wort gibt. Eine Rechnungsnummer wie
 * `RE-2026-0042` bleibt damit unangetastet, und ein Wert ohne Wörterbuch-
 * eintrag ebenfalls - das ist immer noch besser als ein leeres Feld.
 */
function uebersetzeWerte(vars) {
  if (!vars || typeof vars !== 'object') {
    return vars;
  }
  const raus = {};
  for (const [name, wert] of Object.entries(vars)) {
    if (typeof wert === 'string' && /^[A-Z][A-Z0-9_]*$/.test(wert)) {
      const schluessel = `status.${wert}`;
      const text = uebersetze(schluessel);
      raus[name] = text === schluessel ? wert : text;
    } else {
      raus[name] = wert;
    }
  }
  return raus;
}

/**
 * Die Meldung, die ein Mensch zu sehen bekommt.
 *
 * Der Grundsatz: was der Server auf Deutsch sagt, gewinnt - er weiss mehr
 * über den Vorgang als der Browser. Übernommen wird hier nur, was der
 * Server NICHT erklären kann oder nur englisch erklärt:
 *
 * - Es kam gar keine Antwort. axios sagt dazu "Network Error"; für die
 *   betroffene Person ist die Frage aber, ob sie es gleich nochmal
 *   versuchen kann.
 * - Die Antwort kam zu spät. "timeout of 20000ms exceeded" nennt eine
 *   Zahl, die niemanden interessiert.
 * - 429. Die Bremse antwortet als reiner Text, nicht in der Hülle - das
 *   Frontend sah bisher nur "Request failed with status code 429". Genau
 *   der Fall, der einen Menschen trifft, der sein Kennwort dreimal falsch
 *   getippt hat.
 * - 500 aus einem der acht Dienste. Die lautet überall "Internal server
 *   error", weil ein unerwarteter Fehler nichts nach draussen geben darf.
 */
export function meldungFuer(error) {
  const antwort = error?.response;
  const status = antwort?.status ?? 0;
  const huelle = antwort?.data;
  const serverMeldung = typeof huelle === 'object' && huelle !== null ? huelle?.error?.message : null;
  const serverCode = typeof huelle === 'object' && huelle !== null ? huelle?.error?.code : null;

  // Abgebrochen, weil die Seite verlassen wurde - keine Meldung wert.
  if (error?.code === 'ERR_CANCELED') {
    return uebersetze('fehler.abgebrochen');
  }

  if (!antwort) {
    const zeitueberschreitung =
      error?.code === 'ECONNABORTED' ||
      error?.code === 'ETIMEDOUT' ||
      /timeout/i.test(error?.message || '');
    return uebersetze(zeitueberschreitung ? 'fehler.zeitueberschreitung' : 'fehler.keineVerbindung');
  }

  if (status === 429) {
    const sekunden = wartezeitSekunden(antwort.headers);
    return sekunden
      ? uebersetze('fehler.zuVieleAnfragen.warten', { sekunden })
      : uebersetze('fehler.zuVieleAnfragen');
  }

  if (status === 413) {
    return uebersetze('fehler.zuGross');
  }

  if (status === 502 || status === 503 || status === 504) {
    return uebersetze('fehler.dienstNichtErreichbar');
  }

  /* Der Sammeltopf der Dienste. Nur bei 500 ersetzen: ein AppError ohne
     eigenen Code trägt ebenfalls INTERNAL_ERROR, hat dann aber einen
     kleineren Status und eine deutsche Meldung, die stehen bleiben muss. */
  if (status >= 500 && (!serverMeldung || serverCode === 'INTERNAL_ERROR')) {
    return uebersetze('fehler.serverfehler');
  }

  /* Der häufigste Fehler überhaupt: ein Tippfehler im Formular. Der Dienst
     sagt dazu "Request validation failed" - englisch, und vor allem ohne
     jede Angabe, WELCHES Feld gemeint ist. Die Angaben liegen daneben in
     `details.fields`, und genau die werden hier zu einem Satz. */
  if (serverCode === 'VALIDATION_ERROR') {
    const ausFeldern = feldFehlerMeldung(huelle?.error?.details?.fields);
    if (ausFeldern) {
      return ausFeldern;
    }
  }

  /* Ein Übersetzungsschlüssel des Dienstes.
     Der Dienst weiss nicht, welche Sprache am anderen Ende eingestellt ist -
     also schickt er einen Schlüssel statt eines Satzes, und hier wird daraus
     Text. Kennt das Wörterbuch ihn nicht, fällt es weiter unten auf die
     Meldung des Dienstes zurück: ein unübersetzter Satz ist unschön, ein
     leerer Bildschirm wäre schlimmer. */
  const schluessel = typeof huelle === 'object' && huelle !== null ? huelle?.error?.key : null;
  if (schluessel) {
    const voll = `fehler.${schluessel}`;
    const text = uebersetze(voll, uebersetzeWerte(huelle?.error?.vars));
    if (text !== voll) {
      return text;
    }
  }

  if (serverMeldung) {
    return serverMeldung;
  }

  return uebersetze('fehler.unbekannt', { status });
}

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && onUnauthorized) {
      onUnauthorized();
    }
    if (error.response?.status === 403 && onForbidden) {
      /* Auch die Verweigerung geht durch die Uebersetzung. Vorher landete
         hier die Rohmeldung des Dienstes ("No write access to hr") direkt in
         einer Kurzmeldung am Bildschirm. */
      onForbidden(meldungFuer(error));
    }
    const body = error.response?.data;
    return Promise.reject({
      status: error.response?.status || 0,
      body,
      message: meldungFuer(error),
      code: error.code || (typeof body === 'object' ? body?.error?.code : null) || null,
      /* Die Rohmeldung von axios bleibt erhalten - sie gehört ins Protokoll,
         nicht auf den Bildschirm. */
      technisch: error.message,
      fields: typeof body === 'object' ? body?.error?.details?.fields : undefined,
    });
  },
);

/**
 * Tiny envelope unwrapper. The API contract is `{success, data, error, meta?}`;
 * callers want `data` (and optionally `meta`).
 */
export async function apiGet(url, params) {
  const { data } = await apiClient.get(url, { params });
  return { items: data.data, meta: data.meta };
}

export async function apiPost(url, body) {
  const { data } = await apiClient.post(url, body);
  return data.data;
}

export async function apiPut(url, body) {
  const { data } = await apiClient.put(url, body);
  return data.data;
}

export async function apiPatch(url, body) {
  const { data } = await apiClient.patch(url, body);
  return data.data;
}

/**
 * Löschen - mit einem Rumpf, wenn einer mitgegeben wird.
 *
 * Ein DELETE mit Rumpf ist ungewöhnlich, aber hier richtig: seit dem
 * Monatsabschluss löscht der Dienst eine Zeitzeile nicht mehr, er
 * storniert sie - und der Grund dafür gehört an die Zeile. Ihn als
 * Abfrageparameter anzuhängen hiesse, ihn im Zugriffsprotokoll zu
 * verewigen.
 */
export async function apiDelete(url, body) {
  const { data } = await apiClient.delete(url, body ? { data: body } : undefined);
  return data.data;
}
